import { Button, List, notification, Table, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { ReactComponent as IconDownloadExcel } from "../../../assets/images/download_excel.svg";
import { ReactComponent as IconLoaderDot } from "../../../assets/images/loader-dot.svg";
import { ReactComponent as IconError } from "../../../assets/images/error.svg";
import { ReactComponent as IconReload } from "../../../assets/images/reload.svg";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import "./BSAOutput.scss";
import API_SERVICE from "shared/services/api-service";
import {
  setBSAOutputPageNo,
  setBSAOutputPageSize,
} from "shared/redux/bsaOutput-reducer";
import AppLoading from "../AppLoading/AppLoading";
import { saveByteArray } from "shared/services/Utility";

const { Text } = Typography;

interface IBSAOutput {
  setShowBSAOutputDetailsFor: Dispatch<SetStateAction<string>>;
  leadData: any;
}

const BSAOutput: React.FC<IBSAOutput> = (props: IBSAOutput) => {
  const { setShowBSAOutputDetailsFor, leadData } = props;

  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(2);
  const [BSAOutputList, setBSAOutputList] = useState<any>();

  const { isMobile }: any = useSelector((state: RootState) => state.common);
  const { pageNo, pageSize } = useSelector((state: RootState) => state.bsaOutput);

  const dispatch = useDispatch();

  useEffect(() => {
    getBSAOutputList(pageNo, pageSize);
  }, []);

  const getBSAOutputList = (pageNo: number = 1, pageSize: number = 10) => {
    setLoading(true);
    const params = {
      pageNo: pageNo,
      pageSize: pageSize,
      caseId: leadData?.caseDetails?.caseId,
    };
    API_SERVICE.getBSAOutputList(params)
      .then(({ data }) => {
        setBSAOutputList(data?.payload?.content);
        setTotalRecords(data?.payload?.totalElements);
      })
      .catch((e: any) => {
        notification.error({ message: API_SERVICE.handleErrors(e) });
      })
      .finally(() => setLoading(false));
  };

  const downloadBSAOutputExcel = (params: any, fileName: string) => {
    setLoading(true);
    API_SERVICE.downloadBSAOutputExcel(params)
      .then(({ data }) => {
        saveByteArray([data], fileName);
      })
      .catch((e: any) => {
        notification.error({ message: API_SERVICE.handleErrors(e) });
      })
      .finally(() => setLoading(false));
  };

  const handlePaginationChange = (pagination) => {
    let page = pagination.pageSize !== pageSize ? 1 : pagination.current;
    dispatch(setBSAOutputPageNo(page));
    dispatch(setBSAOutputPageSize(pagination.pageSize));
    getBSAOutputList(page, pagination.pageSize); 
    window.scrollTo(0, 0);
  };

  const columns: ColumnsType<any> = [
    {
      title: "File Name",
      dataIndex: ["fileName"],
      key: "fileName",
      render(value, record) {
        const isValue = (value?.length ?? 0) > 0;
        return (
          <span
            style={(isValue && record?.status == "SUCCESS") ? { color: "#4285F4", cursor: "pointer" } : {}}
            onClick={() => {
              if (isValue && record?.status == "SUCCESS") {
                setShowBSAOutputDetailsFor(record?.fileName);
              }
            }}
          >
            {isValue ? value : "--"}
          </span>
        );
      },
    },
    {
      title: "Case ID",
      dataIndex: ["caseId"],
      key: "caseId",
    },
    {
      title: "File Used",
      dataIndex: ["fileUsed"],
      key: "fileUsed",
      width: 150,
      render(value, record) {
        return (
          <Tooltip title={value?.join(", ")}>
            <div
              style={{
                maxWidth: "150px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value?.join(", ")}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Banks Involved",
      dataIndex: ["banks"],
      key: "banks",
      width: 150,
      render(value, record) {
        return (
          <Tooltip title={value?.join(", ")}>
            <div
              style={{
                maxWidth: "150px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value?.join(", ")}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Duration (Months)",
      dataIndex: ["durationInMonths"],
      key: "durationInMonths",
    },
    {
      title: "BSA Run Date",
      dataIndex: ["bsaRunDate"],
      key: "bsaRunDate",
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: 60,
      render(value, record) {
        if (record?.status == "FAILED") {
          return (
            <Tooltip title="Please try again">
              <IconError />
            </Tooltip>
          );
        } else if (record?.status == "IN_PROGRESS") {
          return (
            <Tooltip title="In Progress, Please Wait">
              <IconLoaderDot />
            </Tooltip>
          );
        } else if (record?.status == "SUCCESS" && record?.bsaProvider != null) {
          return (
            <Button type="text" onClick={() =>{
              if (record?.bsaProvider == "FINBOX" && record?.finboxOutputDocumentIds != null) {
                record?.finboxOutputDocumentIds?.map((finboxOutputDocumentIds) => {
                  const params = { finboxOutputDocumentId: finboxOutputDocumentIds};
                  const fileName = finboxOutputDocumentIds ? `${finboxOutputDocumentIds}.xlsx` : "bsaOutput.xlsx";
                  downloadBSAOutputExcel(params, fileName)
                })
              } else {
                const params = { filePath: record?.filePath };
                const fileName = record?.filePath?.split("/")?.at(-1) ?? "bsaOutput.xlsx";
                downloadBSAOutputExcel(params, fileName)}
              }
            }>
            <IconDownloadExcel />
          </Button>
          );
        }
        return (<></>);
      },
    },
  ];

  const renderListItem = (item: any) => {
    const isFileName = (item?.fileName?.length ?? 0) > 0;
    return (
      <>
        <List.Item style={{ padding: "0px 0px" }}>
          <div className="BSA-output-list-item-div">
            <div className="BSA-output-list-item-left">
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">File Name</span>
                <div
                  style={{ color: "#4285F4", cursor: "pointer" }}
                  onClick={() => {
                    if (isFileName) {
                      setShowBSAOutputDetailsFor(item?.fileName);
                    }
                  }}
                >
                  {isFileName ? item?.fileName : "--"}
                </div>
              </div>
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">Banks</span>
                <Tooltip title={item?.banks?.join(", ")}>
                  <Text ellipsis>{item?.banks?.join(",")}</Text>
                </Tooltip>
              </div>
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">BSA Run Date</span>
                <div>{item?.bsaRunDate}</div>
              </div>
            </div>
            <div className="BSA-output-list-item-right">
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">Files Used</span>
                <Tooltip title={item?.fileUsed?.join(", ")}>
                  <Text ellipsis>{item?.fileUsed?.join(", ")}</Text>
                </Tooltip>
              </div>
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">
                  Duration (Months)
                </span>
                <div>{item?.durationInMonths}</div>
              </div>
              <div className="BSA-output-list-item">
                <span className="BSA-output-list-item-title">Action</span>
                <div>
                  {item?.error ? (
                    <Tooltip title="Please try again" mouseEnterDelay={2}>
                      <IconError />
                    </Tooltip>
                  ) : item?.progress ? (
                    <Tooltip
                      title="In Progress, Please Wait"
                      mouseEnterDelay={2}
                    >
                      <IconLoaderDot />
                    </Tooltip>
                  ) : (
                    <Button type="text">
                      <IconDownloadExcel />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </List.Item>
      </>
    );
  };

  const getColumns = () => {
    const co = columns.filter((col) => col.key !== (leadData?.caseDetails?.caseId ? "caseId" : ""));
    return co;
  };

  return (
    <div>
      <div style={{display:'flex', justifyContent:'right', marginTop:'-45px', marginBottom:'20px'}}>
      <Tooltip title="Refresh">
        <Button
          className="custome-btn-icon"
          style={{ '--bgColor': "black", '--width': "16px" } as any}
          onClick={() => {
            dispatch(setBSAOutputPageNo(1));
            dispatch(setBSAOutputPageSize(10));
            getBSAOutputList()
          }}
          icon={<IconReload />}
          size={"small"}
        />
      </Tooltip>
      </div>
      {isMobile ? (
        <List
          style={{ width: "100%" }}
          grid={{ gutter: 24, column: 1, xs: 1, sm: 1, md: 1, lg: 1 }}
          pagination={{
            onChange: handlePaginationChange,
            current: pageNo,
            pageSize: pageSize,
            total: totalRecords,
          }}
          itemLayout="horizontal"
          dataSource={BSAOutputList}
          renderItem={renderListItem}
        />
      ) : (
        <Table
          size="small"
          scroll={{ x: "max-content" }}
          dataSource={BSAOutputList}
          columns={getColumns()}
          pagination={{
            current: pageNo,
            pageSize: pageSize,
            total: totalRecords,
          }}
          onChange={handlePaginationChange}
        />
      )}
      {loading && <AppLoading />}
    </div>
  );
};

export default BSAOutput;
